/**
 * MechaGuard - Main Entry Point
 *
 * 负责应用级编排：
 *   - 创建 Renderer / InputHandler / UI 组件 / Game
 *   - 把 Game 抛出的 hooks 转成具体的 UI 更新
 *   - 管理场景切换（主菜单 / 游玩 / 暂停 / 结算）与主循环
 *
 * 游戏逻辑一律在 Game 内，本文件不包含任何玩法规则。
 */

import { Renderer } from './core/Renderer.js';
import { InputHandler } from './core/InputHandler.js';
import { GameState, GameScene } from './core/GameState.js';
import { Game } from './core/Game.js';
import { createStorage } from './systems/Storage.js';
import { CustomMapStore } from './maps/CustomMaps.js';
import { HUD } from './ui/HUD.js';
import { TowerMenu } from './ui/TowerMenu.js';
import { TowerPanel } from './ui/TowerPanel.js';
import { SkillBar } from './ui/SkillBar.js';
import { LevelSelect } from './ui/LevelSelect.js';
import { MapEditor } from './ui/MapEditor.js';
import { AchievementPanel } from './ui/AchievementPanel.js';
import { LeaderboardPanel } from './ui/LeaderboardPanel.js';
import { Toast } from './ui/Toast.js';
import { formatTime, formatNumber } from './ui/format.js';
import { TOWER_TYPES } from './tower-types/index.js';
import { EnemyTypes } from './enemy-types/index.js';
import { WAVE_DEFAULTS } from './systems/WaveSystem.js';
import { balanceConfig, GLOBAL_DEFAULTS } from './config/BalanceConfig.js';
import { OpsPanel } from './ui/OpsPanel.js';

/** 单帧最大时间步长（秒）：切后台回来时避免一帧推进过多 */
const MAX_DELTA = 0.05;

class Main {
  constructor() {
    this.canvas = document.getElementById('game-canvas');

    this.gameState = new GameState();
    this.hud = new HUD();
    this.towerMenu = new TowerMenu();
    this.towerPanel = new TowerPanel();
    this.skillBar = new SkillBar();
    this.levelSelect = new LevelSelect();
    this.mapEditor = new MapEditor();
    this.achievementPanel = new AchievementPanel();
    this.leaderboardPanel = new LeaderboardPanel();
    this.toast = new Toast();

    /** 持久化后端；isPersistent=false 表示只能存内存（隐私模式） */
    this.storage = null;
    this.storagePersistent = true;

    /** 自定义地图仓库（init 里创建，依赖 storage） */
    this.customMaps = null;

    this.renderer = null;
    this.input = null;
    this.game = null;

    /** 运营调参入口：仅地址栏带 ?ops=1 时启用 */
    this.opsEnabled = new URLSearchParams(location.search).has('ops');
    this.opsPanel = null;

    this.running = false;
    this._loopId = 0;
    this._lastTimestamp = 0;
  }

  /* ===================================================
   * 初始化
   * =================================================== */

  init() {
    this.renderer = new Renderer(this.canvas);
    this.input = new InputHandler(this.canvas, this.renderer);

    // 持久化后端：优先 localStorage，不可用（隐私模式等）时退化为内存
    const storageInfo = createStorage();
    this.storage = storageInfo.backend;
    this.storagePersistent = storageInfo.isPersistent;

    // 运营调参：中心配置层（?ops=1 时由 OpsPanel 暴露给运营人员调整）
    balanceConfig.init({
      towers: Object.fromEntries(TOWER_TYPES.map(t => [t.id, { ...t.stats, cost: t.cost }])),
      enemies: Object.fromEntries(
        Object.values(EnemyTypes).map(e => [e.id, JSON.parse(JSON.stringify(e))])
      ),
      waves: { ...WAVE_DEFAULTS },
      global: { ...GLOBAL_DEFAULTS },
      storage: this.storage,
    });
    balanceConfig.onChange = () => this._applyBalanceLive();

    // ---- UI ----
    this.hud.init();
    this.toast.init();

    this.towerPanel.init({
      onUpgrade: () => this.game?.upgradeSelected(),
      onSell: () => this.game?.sellSelected(),
      onClose: () => this.game?.handleSecondary(),
    });

    this.towerMenu.init((id) => this._onTowerMenuSelect(id));

    // 自定义地图仓库：必须在 LevelSelect 与 Game 之前建好 ——
    // 它的构造函数会把存档里的地图注册进 MapData，
    // 否则 createMap() 找不到自定义关卡 id。
    this.customMaps = new CustomMapStore({ storage: this.storage });

    // 关卡选择在主菜单里，必须在创建 Game 之前就绪
    this.levelSelect.init({
      onEdit: (id) => this.openEditor(id),
      onDelete: (id) => this.deleteCustomMap(id),
      onNew: () => this.openEditor(null),
    });
    this.levelSelect.setCustomMaps(this.customMaps.list());

    this.mapEditor.init({
      onSave: (draft, play) => this.saveCustomMap(draft, play),
      onClose: () => this.showMainMenu(),
    });

    this.achievementPanel.init({ onClose: () => this.achievementPanel.close() });
    this.leaderboardPanel.init({ onClose: () => this.leaderboardPanel.close() });

    // ---- 游戏世界 ----
    this.game = new Game({
      renderer: this.renderer,
      levelId: this.levelSelect.getSelected(),
      storage: this.storage,
      hooks: {
        onGoldChange: (gold) => {
          this.hud.setGold(gold);
          this.towerMenu.setGold(gold);
          this.towerPanel.setGold(gold);
        },
        onLivesChange: (lives) => this.hud.setLives(lives),
        onWaveChange: (current, total) => this.hud.setWave(current, total),
        onPrepChange: (seconds, isBoss) => this.hud.setPrep(seconds, isBoss),
        onBuildTypeChange: (id) => this.towerMenu.setSelected(id),
        onSelectionChange: (tower) => {
          if (tower) this.towerPanel.show(tower);
          else this.towerPanel.hide();
        },
        onToast: (message) => this.toast.show(message),
        onVictory: (stats) => this._onVictory(stats),
        onDefeat: (stats) => this._onDefeat(stats),

        // ---- Phase 3：能量 / 技能 / 关卡 ----
        onEnergyChange: () => this.skillBar.sync(),
        onSkillStateChange: () => this.skillBar.sync(),
        onPendingSkillChange: (id) => this.skillBar.setPending(id),
        onShieldChange: (shield) => this.hud.setShield(shield),
        onLevelChange: (level) => this.hud.setLevelName(level.name),

        // ---- Phase 4：成就 / 排行 ----
        onAchievement: (achievement) => {
          this.toast.show(
            `${achievement.icon} 成就解锁：${achievement.name}`,
            { duration: 2800, variant: 'gold' }
          );
        },
        onRunFinalized: (result) => {
          if (result.isBest) {
            this.toast.show(
              `🏆 新纪录！本关最高分 ${formatNumber(result.score)}`,
              { duration: 3200, variant: 'gold' }
            );
          }
        },
      },
    });

    // 技能栏需要拿到 SkillSystem 实例才能读取能量/冷却
    this.skillBar.init(this.game.skills, (id) => this._onSkillSelect(id));

    // 运营调参面板（仅 ?ops=1 时启用）
    this.opsPanel = new OpsPanel(balanceConfig);
    if (this.opsEnabled) this.opsPanel.init();

    this._bindCanvasInput();
    this._bindUIEvents();
    this._bindAudio();

    this._syncAudioButton();
    this.showMainMenu();
    this._initDebugOverlay();

    console.log(
      '%cMechaGuard ready%c  |  Phase 4：程序化音频 · 成就 · 排行榜',
      'color:#00ffff;font-weight:bold',
      'color:#8ab4d8'
    );
    console.log('提示：地址栏加 ?debug=1 可开启屏幕上的帧率/画布重建读数');
  }

  /* ===================================================
   * 音频
   * =================================================== */

  /**
   * 浏览器要求 AudioContext 必须在用户手势里创建。
   * 这里在首次任意交互时解锁，之后音频一直可用。
   */
  _bindAudio() {
    const unlock = () => {
      this.game.audio.unlock();
      this._syncAudioButton();
    };

    // once:true —— 两个都留着也安全，unlock() 重复调用只做 resume
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    // 声音开关单独绑定：不能用通用 click 音，
    // 否则「从关到开」那一下会因为当时还是静音而听不到反馈
    const audioBtn = document.getElementById('audio-btn');
    if (audioBtn) {
      audioBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.game.audio.toggle();
        this._syncAudioButton();
        this.game.sfx('click');
      });
    }
  }

  _syncAudioButton() {
    const btn = document.getElementById('audio-btn');
    if (!btn || !this.game) return;

    const on = this.game.audio.enabled;
    btn.textContent = on ? '🔊' : '🔇';
    btn.classList.toggle('is-off', !on);
    btn.title = on ? '关闭声音' : '开启声音';
  }

  /* ===================================================
   * 输入绑定
   * =================================================== */

  _bindCanvasInput() {
    this.input.onTap = (wx, wy) => {
      if (this.gameState.current !== GameScene.PLAYING) return;
      this.game.handleTap(wx, wy);
    };

    this.input.onSecondary = () => {
      if (this.gameState.current !== GameScene.PLAYING) return;
      this.game.handleSecondary();
    };

    this.input.onHover = (wx, wy) => {
      if (this.gameState.current !== GameScene.PLAYING) return;
      this.game.handleHover(wx, wy);
    };
  }

  _bindUIEvents() {
    const on = (id, handler) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.game.sfx('click');
          handler();
        });
      }
    };

    // 主菜单
    on('start-btn', () => this.startGame());
    on('controls-btn', () => this._showOverlay('help-overlay'));
    on('help-close-btn', () => this._hideOverlay('help-overlay'));
    on('achievements-btn', () => this.achievementPanel.open(this.game.achievements));
    on('leaderboard-btn', () => this.leaderboardPanel.open(this.game.leaderboard));

    // 暂停
    on('pause-btn', () => this.togglePause());
    on('resume-btn', () => this.togglePause());
    on('restart-btn', () => this.restart());
    on('quit-btn', () => this.showMainMenu());
    on('pause-achievements-btn', () => this.achievementPanel.open(this.game.achievements));
    on('pause-leaderboard-btn', () => this.leaderboardPanel.open(this.game.leaderboard));

    // 波次控制
    on('next-wave-btn', () => this.game.callNextWave());

    // 结算
    on('victory-restart-btn', () => this.restart());
    on('victory-menu-btn', () => this.showMainMenu());
    on('victory-board-btn', () => {
      this.leaderboardPanel.open(this.game.leaderboard, this.game.levelId);
    });
    on('defeat-restart-btn', () => this.restart());
    on('defeat-menu-btn', () => this.showMainMenu());
    on('defeat-board-btn', () => {
      this.leaderboardPanel.open(this.game.leaderboard, this.game.levelId);
    });

    // 键盘快捷键
    window.addEventListener('keydown', (e) => {
      const playing = this.gameState.current === GameScene.PLAYING;
      const paused = this.gameState.current === GameScene.PAUSED;

      if (e.code === 'Space' || e.key === 'p' || e.key === 'P') {
        if (playing || paused) {
          e.preventDefault();
          this.togglePause();
        }
        return;
      }

      if (e.key === 'Escape') {
        if (playing || paused) this.game.handleSecondary();
        return;
      }

      // 数字键 1~5 快速选择塔
      if (playing && e.key >= '1' && e.key <= '5') {
        const idx = Number(e.key) - 1;
        const buttons = document.querySelectorAll('.tower-btn');
        if (buttons[idx]) this._onTowerMenuSelect(buttons[idx].dataset.id);
      }
    });

    // 切到后台：自动暂停并挂起音频（否则后台会一直在响）
    document.addEventListener('visibilitychange', () => {
      const audio = this.game ? this.game.audio : null;

      if (document.hidden) {
        if (this.gameState.current === GameScene.PLAYING) this.togglePause();
        // 切到后台立即落盘成就累计统计，避免防抖定时器未触发而丢失
        if (this.game) this.game.achievements.flush();
        if (audio && audio.ctx) {
          try { audio.ctx.suspend(); } catch { /* 忽略 */ }
        }
      } else if (audio && audio.ctx && audio.enabled) {
        try { audio.ctx.resume(); } catch { /* 忽略 */ }
      }
    });
  }

  _onTowerMenuSelect(id) {
    if (this.gameState.current !== GameScene.PLAYING) return;

    // 再次点击同一个按钮 = 取消建造
    if (this.game.buildType === id) {
      this.game.handleSecondary();
      return;
    }

    this.game.setBuildType(id);
  }

  /** 技能栏点击 */
  _onSkillSelect(id) {
    if (this.gameState.current !== GameScene.PLAYING) return;
    this.game.castSkill(id);
  }

  /** 运营视图改动后立即作用到当前对局（塔即时；敌人/波次下波或新刷怪生效） */
  _applyBalanceLive() {
    const game = this.game;
    if (!game) return;

    // 塔：立即作用到场上已有塔
    for (const t of game.towers) {
      t.applyStats(balanceConfig.getTowerStats(t.id, t.constructor.META.stats));
    }
    if (game.selectedTower) this.towerPanel.show(game.selectedTower);

    // 建造栏：售价文案与悬浮提示按最新配置刷新
    this.towerMenu.syncConfig();

    // 波次：重建波次表（下一波生效）
    game.rebuildWaves();
  }

  /* ===================================================
   * 场景切换
   * =================================================== */

  startGame() {
    this._hideAllOverlays();
    document.getElementById('loading-screen').classList.remove('active');
    document.getElementById('game-container').classList.remove('hidden');

    this.hud.reset();
    this.toast.clear();
    this.towerPanel.hide();

    // 重置世界（按主菜单选中的关卡，同时重置经济、波次、技能、相机）
    this.game.reset(this.levelSelect.getSelected());

    // 容器可见后重新测量尺寸，再让相机适配整张地图
    this.renderer.resize();
    this.renderer.fitToBounds(this.game.map.width, this.game.map.height);

    this.gameState.reset();                 // -> MENU
    this.gameState.set(GameScene.PLAYING);  // -> PLAYING
    this._startLoop();
  }

  restart() {
    this.startGame();
  }

  /* ===================================================
   * 自定义地图
   * =================================================== */

  /**
   * 打开地图编辑器。
   * @param {string|null} id 传 id 表示编辑已有地图，null 表示新建
   */
  openEditor(id) {
    if (!this.customMaps) return;

    const map = id ? this.customMaps.get(id) : null;

    // 编辑器覆盖在主菜单之上，先把主循环停掉：
    // 留着它只会白白跑渲染，而编辑器自己会重绘画布
    this._stopLoop();
    this.gameState.set(GameScene.MENU);

    this._hideAllOverlays();

    const root = document.getElementById('map-editor');
    if (root) root.classList.remove('hidden');

    // 必须等 overlay 显示出来（脱离 display:none）之后再量尺寸，
    // 否则 clientWidth 是 0，网格会被压成最小尺寸
    this.mapEditor.open(map);
  }

  /**
   * 保存编辑器草稿。
   * @param {object} draft
   * @param {boolean} play 保存后是否立刻开始试玩
   */
  saveCustomMap(draft, play) {
    if (!this.customMaps) return;

    const result = this.customMaps.save(draft);

    if (!result.ok) {
      // 校验在编辑器里已经实时做过一遍，走到这里通常是被别处拦截
      this.toast.show(result.errors[0] || '保存失败');
      return;
    }

    // 同步列表并选中新地图
    this.levelSelect.setCustomMaps(this.customMaps.list());
    this.levelSelect.setSelected(result.level.id);

    if (play) {
      this.startGame();
      this.toast.show(`试玩「${result.level.name}」`);
    } else {
      this.toast.show(`已保存「${result.level.name}」`);
      this.showMainMenu();
    }
  }

  /** 删除一张自定义地图（二次确认，删除不可撤销） */
  deleteCustomMap(id) {
    if (!this.customMaps) return;

    const map = this.customMaps.get(id);
    if (!map) return;

    if (!window.confirm(`删除地图「${map.name}」？此操作不可撤销。`)) return;

    this.customMaps.remove(id);
    this.levelSelect.setCustomMaps(this.customMaps.list());
    this.toast.show(`已删除「${map.name}」`);
  }

  showMainMenu() {
    this._stopLoop();
    this.gameState.set(GameScene.MENU);

    // 主循环已停，_updateMusicMode 不会被调用，这里显式把配乐切回菜单曲
    if (this.game) this.game.audio.setMusicMode('menu');

    document.getElementById('game-container').classList.add('hidden');
    this._hideAllOverlays();
    document.getElementById('loading-screen').classList.remove('active');
    document.getElementById('main-menu').classList.remove('hidden');

    this.toast.clear();
    this.towerPanel.hide();
  }

  togglePause() {
    if (this.gameState.current === GameScene.PLAYING) {
      this.gameState.set(GameScene.PAUSED);
      this._showOverlay('pause-overlay');
    } else if (this.gameState.current === GameScene.PAUSED) {
      this.gameState.set(GameScene.PLAYING);
      this._hideOverlay('pause-overlay');
      this._lastTimestamp = 0; // 恢复时不让 dt 暴涨
    }
  }

  _onVictory(stats) {
    this.gameState.set(GameScene.VICTORY);
    this._renderStats('victory-stats', stats);
    this._showOverlay('victory-overlay');
  }

  _onDefeat(stats) {
    this.gameState.set(GameScene.DEFEAT);
    this._renderStats('defeat-stats', stats);
    this._showOverlay('defeat-overlay');
  }

  /* ===================================================
   * 主循环
   * =================================================== */

  _startLoop() {
    this._loopId += 1;
    const loopId = this._loopId;

    this.running = true;
    this._lastTimestamp = 0;

    requestAnimationFrame((t) => this._loop(t, loopId));
  }

  _stopLoop() {
    this.running = false;
    this._loopId += 1; // 让仍在排队的旧帧回调失效
  }

  _loop(timestamp, loopId) {
    if (!this.running || loopId !== this._loopId) return;

    if (!this._lastTimestamp) this._lastTimestamp = timestamp;

    // 原始帧间隔（未夹取），用于调试浮层的真实帧耗时
    const rawMs = timestamp - this._lastTimestamp;

    let dt = rawMs / 1000;
    this._lastTimestamp = timestamp;

    if (!Number.isFinite(dt) || dt < 0) dt = 0;
    dt = Math.min(dt, MAX_DELTA);

    if (this.gameState.current === GameScene.PLAYING) {
      this.game.update(dt);
    }

    // 尺寸变更必须在这里、在绘制之前应用。
    // 给 canvas.width 赋值会立刻清空画布，如果放在 resize 事件里直接做，
    // 到下一次绘制之间就会闪过一帧空白 —— 这正是移动端「突然黑一下」的来源。
    // 放在帧首，「清空」和「重绘」落在同一帧内，不会出现空白帧。
    this.renderer.applyPendingResize();

    this.game.render();

    if (this._debug) this._debugSample(rawMs, timestamp);

    requestAnimationFrame((t) => this._loop(t, loopId));
  }

  /* ===================================================
   * 调试浮层
   * =================================================== */

  /**
   * 开启调试浮层：地址栏加 ?debug=1（或 #debug）。
   *
   * 手机上没有控制台，而「黑一下闪动」有两个完全不同的可能原因：
   *   A. 帧耗时过长（重绘卡顿，合成时表现为空白）
   *   B. 画布被反复重建（每次给 canvas.width 赋值都会清空）
   * 两者的修法完全不同，所以必须能在屏幕上把它们区分开 ——
   * 「画布重建」计数如果一直在涨，就是 B；否则看帧耗时峰值判断 A。
   */
  _initDebugOverlay() {
    const on = window.location.search.indexOf('debug=1') !== -1
      || window.location.hash === '#debug';
    if (!on) return;

    const el = document.createElement('div');
    el.id = 'debug-overlay';
    document.body.appendChild(el);

    this._debug = {
      el,
      frames: 0,
      accMs: 0,
      maxMs: 0,
      lastReport: performance.now(),
      // 记录上次报告的读数，方便判断数值是否还在增长
      lastResizeCount: -1,
    };
  }

  /** 每秒刷新一次读数（不是每帧刷 DOM，避免测量本身造成卡顿） */
  _debugSample(rawMs, timestamp) {
    const d = this._debug;

    d.frames += 1;
    d.accMs += rawMs;
    if (rawMs > d.maxMs) d.maxMs = rawMs;

    const elapsed = timestamp - d.lastReport;
    if (elapsed < 1000) return;

    const fps = (d.frames * 1000) / elapsed;
    const avgMs = d.accMs / d.frames;
    const canvas = this.renderer.canvas;
    const dpr = window.devicePixelRatio || 1;

    const growing = d.lastResizeCount >= 0 && this.renderer.resizeCount > d.lastResizeCount;
    d.lastResizeCount = this.renderer.resizeCount;

    d.el.textContent =
      `${fps.toFixed(0)} fps  帧均值 ${avgMs.toFixed(1)}ms  峰值 ${d.maxMs.toFixed(0)}ms\n`
      + `画布重建 ${this.renderer.resizeCount} 次${growing ? ' ⚠️增长中' : ''}\n`
      + `${canvas.width}x${canvas.height} @dpr${dpr}  视口 ${window.innerWidth}x${window.innerHeight}`;

    d.frames = 0;
    d.accMs = 0;
    d.maxMs = 0;
    d.lastReport = timestamp;
  }

  /* ===================================================
   * 辅助
   * =================================================== */

  _showOverlay(id) {
    document.getElementById(id)?.classList.remove('hidden');
  }

  _hideOverlay(id) {
    document.getElementById(id)?.classList.add('hidden');
  }

  _hideAllOverlays() {
    [
      'pause-overlay',
      'main-menu',
      'help-overlay',
      'victory-overlay',
      'defeat-overlay',
      'map-editor',
    ].forEach(id => this._hideOverlay(id));
  }

  _renderStats(elementId, stats) {
    const el = document.getElementById(elementId);
    if (!el) return;

    el.innerHTML = '';

    // 自定义地图不参与排名：说明原因，而不是显示一个让人困惑的「未上榜」
    const rankText = stats.ranked === false
      ? '自定义地图 · 不计入'
      : !stats.rank
        ? '未上榜'
        : stats.isBest
          ? `🏆 第 ${stats.rank} 名 · 新纪录`
          : `第 ${stats.rank} 名`;

    const rows = [
      ['关卡', stats.levelName],
      ['得分', formatNumber(stats.score)],
      ['本关排名', rankText],
      ['守住波次', `${stats.wavesCleared} / ${stats.totalWaves}`],
      ['击杀总数', String(stats.kills)],
      ['剩余生命', String(stats.lives)],
      ['建造塔数', String(stats.towers)],
      ['成就进度', `${stats.achievementsUnlocked} / ${stats.achievementsTotal}`],
      ['用时', formatTime(stats.elapsed)],
    ];

    for (const [label, value] of rows) {
      const box = document.createElement('div');
      box.className = 'result-stat';

      const l = document.createElement('span');
      l.className = 'result-stat-label';
      l.textContent = label;

      const v = document.createElement('span');
      v.className = 'result-stat-value';
      v.textContent = value;

      box.append(l, v);
      el.appendChild(box);
    }
  }
}

/* ===================================================
 * 启动
 * =================================================== */

const app = new Main();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => app.init());
} else {
  app.init();
}
